import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFirestoreDoc } from '../utils/useFirestoreDoc';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../utils/i18n';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import './Footer.css';

const Footer = () => {
  const { lang } = useLanguage();
  const { data: firestoreContent } = useFirestoreDoc('web-contents', 'footer');
  
  // Contact form state
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Fallback content if Firestore data doesn't exist yet
  const defaultContent = {
    brand: {
      title: { en: "Peace & Freedom Movement", de: "Frieden & Freiheit Bewegung", fa: "جنبش صلح و آزادی" },
      description: { 
        en: "Advancing human dignity, freedom and peaceful coexistence through principled civic action.",
        de: "Förderung der Menschenwürde, Freiheit und friedlichen Koexistenz durch prinzipiengeleitetes bürgerschaftliches Handeln.",
        fa: "پیشبرد کرامت انسانی، آزادی و همزیستی مسالمت‌آمیز از طریق اقدامات مدنی اصولی."
      }
    },
    social: {
      twitter: "https://x.com/pfmdeutschland",
      instagram: "https://www.instagram.com/samimyawerofficial",
      facebook: "https://www.facebook.com/share/1BHSU3yUMH/",
      youtube: "https://youtube.com/channel/UCSFZelP0NfYEiRsfFVgHPbA",
      tiktok: "https://www.tiktok.com/@pfmmovement"
    },
    appDownload: {
      label: { en: "Download our app", de: "Laden Sie unsere App herunter", fa: "دانلود اپلیکیشن ما" },
      appleStore: "https://apps.apple.com",
      googlePlay: "https://play.google.com"
    },
    links: {
      organization: {
        title: { en: "Organization", de: "Organisation", fa: "سازمان" },
        items: [
          { label: { en: "About Us", de: "Über uns", fa: "درباره ما" }, href: "/about-us" },
          { label: { en: "Our Values", de: "Unsere Werte", fa: "ارزش‌های ما" }, href: "/about-us#values" },
          { label: { en: "What We Do", de: "Was wir tun", fa: "کارهای ما" }, href: "/about-us#work" },
          { label: { en: "News", de: "Nachrichten", fa: "اخبار" }, href: "/news" }
        ]
      },
      getInvolved: {
        title: { en: "Get Involved", de: "Mitmachen", fa: "مشارکت کنید" },
        items: [
          { label: { en: "Join the Movement", de: "Bewegung beitreten", fa: "به جنبش بپیوندید" }, href: "/membership" },
          { label: { en: "Donate", de: "Spenden", fa: "کمک مالی" }, href: "#donate" },
          { label: { en: "Volunteer", de: "Freiwillig helfen", fa: "داوطلب شوید" }, href: "#volunteer" },
          { label: { en: "Partner With Us", de: "Partner werden", fa: "همکاری با ما" }, href: "#partner" }
        ]
      },
      contact: {
        title: { en: "Contact", de: "Kontakt", fa: "تماس" },
        phone: "+1 (832) 259-5243",
        email: "info@pfm-movement.org",
        city: { en: "Houston, Texas", de: "Houston, Texas", fa: "هیوستون، تگزاس" },
        country: { en: "United States", de: "Vereinigte Staaten", fa: "ایالات متحده" }
      }
    },
    legal: {
      copyright: { 
        en: "© 2026 Peace & Freedom Movement. All rights reserved.",
        de: "© 2026 Frieden & Freiheit Bewegung. Alle Rechte vorbehalten.",
        fa: "© ۲۰۲۶ جنبش صلح و آزادی. تمامی حقوق محفوظ است."
      },
      links: [
        { label: { en: "Privacy Policy", de: "Datenschutz", fa: "حریم خصوصی" }, href: "/policy#privacy" },
        { label: { en: "Terms & Conditions", de: "AGB", fa: "شرایط و ضوابط" }, href: "/policy#terms" },
        { label: { en: "Accessibility", de: "Barrierefreiheit", fa: "دسترسی‌پذیری" }, href: "/policy#accessibility" }
      ]
    },
    badge: {
      text: { 
        en: "Nonprofit Organization Est. 2024",
        de: "Gemeinnützige Organisation Gegr. 2024",
        fa: "سازمان غیرانتفاعی تاسیس ۲۰۲۴"
      }
    }
  };

  // Use Firestore content if available, otherwise use default
  const content = firestoreContent || defaultContent;

  const normalizeFooterHref = (href, label) => {
    if (!href) return href;

    const labelText = typeof label === 'string' ? label.toLowerCase() : '';

    if (href === '#donate') return '/membership#donate';
    if (href === '#volunteer') return '/membership/register?type=volunteer';
    if (href === '#partner') return 'mailto:info@pfm-movement.org?subject=Partnership%20Inquiry';
    if (href === '/policy#privacy' || href === '/policy#terms' || href === '/policy#accessibility') return href;

    if (labelText.includes('donate') || labelText.includes('کمک مالی') || labelText.includes('spenden')) {
      return '/membership#donate';
    }
    if (labelText.includes('volunteer') || labelText.includes('داوطلب') || labelText.includes('freiwillig')) {
      return '/membership/register?type=volunteer';
    }
    if (labelText.includes('partner') || labelText.includes('همکاری') || labelText.includes('partner werden')) {
      return 'mailto:info@pfm-movement.org?subject=Partnership%20Inquiry';
    }

    return href;
  };

  const renderInternalLink = (href, label, className) => {
    const normalizedHref = normalizeFooterHref(href, label);
    if (!normalizedHref) return <span className={className}>{label}</span>;

    if (normalizedHref.startsWith('/')) {
      return <Link to={normalizedHref} className={className}>{label}</Link>;
    }

    return <a href={normalizedHref} className={className}>{label}</a>;
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      setSubmitStatus({ type: 'error', message: t({ en: 'Please fill in all fields', de: 'Bitte füllen Sie alle Felder aus', fa: 'لطفاً همه فیلدها را پر کنید' }, lang) });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await addDoc(collection(db, 'contact-messages'), {
        name: contactForm.name,
        email: contactForm.email,
        message: contactForm.message,
        createdAt: serverTimestamp(),
        status: 'new'
      });

      setSubmitStatus({ type: 'success', message: t({ en: 'Message sent! We will get back to you shortly.', de: 'Nachricht gesendet! Wir werden uns in Kürze bei Ihnen melden.', fa: 'پیام ارسال شد! به زودی با شما تماس خواهیم گرفت.' }, lang) });
      setContactForm({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus({ type: 'error', message: t({ en: 'Failed to send message. Please try again.', de: 'Nachricht senden fehlgeschlagen. Bitte versuchen Sie es erneut.', fa: 'ارسال پیام ناموفق بود. لطفاً دوباره امتحان کنید.' }, lang) });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <img
                src="/pfm_logo.avif"
                alt="PFM Movement"
                className="footer-logo"
                loading="lazy"
                decoding="async"
              />
              <h3>{t(content.brand?.title, lang)}</h3>
              <p>{t(content.brand?.description, lang)}</p>
              <div className="footer-social">
                <a href={content.social?.twitter} target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a href={content.social?.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                  </svg>
                </a>
                <a href={content.social?.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href={content.social?.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
                <a href={content.social?.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.32 6.32 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z"/>
                  </svg>
                </a>
              </div>

              <div className="footer-app-download">
                <p className="app-download-label">{t(content.appDownload?.label, lang)}</p>
                <div className="app-download-buttons">
                  <a href={content.appDownload?.appleStore} target="_blank" rel="noopener noreferrer" className="app-store-btn apple-btn" aria-label="Download on the App Store">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="#000000">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    <span>
                      <small>Download on the</small>
                      App Store
                    </span>
                  </a>
                  <a href={content.appDownload?.googlePlay} target="_blank" rel="noopener noreferrer" className="app-store-btn google-btn" aria-label="Get it on Google Play">
                    <svg width="26" height="26" viewBox="0 0 24 24">
                      <path fill="#EA4335" d="M3.18 23.76c.3.17.64.24.99.2l12.81-11.75-2.65-2.65-11.15 14.2z"/>
                      <path fill="#4285F4" d="M.5 1.27C.19 1.6 0 2.1 0 2.74v18.52c0 .64.19 1.14.51 1.47l.08.07 10.37-10.37v-.24L.58 1.2l-.08.07z"/>
                      <path fill="#FBBC05" d="M21.38 10.29l-2.95-1.7-2.94 2.94 2.94 2.94 2.96-1.71c.85-.49.85-1.28-.01-1.47z"/>
                      <path fill="#34A853" d="M3.18.24L14.33 11.4l-2.65 2.65L1.17.3C1.48-.04 2.53.04 3.18.24z"/>
                    </svg>
                    <span>
                      <small>Get it on</small>
                      Google Play
                    </span>
                  </a>
                </div>
              </div>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4>{t(content.links?.organization?.title, lang)}</h4>
                <ul>
                  {content.links?.organization?.items?.map((item, idx) => (
                    <li key={idx}>{renderInternalLink(item.href, t(item.label, lang))}</li>
                  ))}
                </ul>
              </div>

              <div className="footer-column">
                <h4>{t(content.links?.getInvolved?.title, lang)}</h4>
                <ul>
                  {content.links?.getInvolved?.items?.map((item, idx) => (
                    <li key={idx}>{renderInternalLink(item.href, t(item.label, lang))}</li>
                  ))}
                </ul>
              </div>

              <div className="footer-column">
                <h4>{t(content.links?.contact?.title, lang)}</h4>
                <form className="footer-contact-form" onSubmit={handleContactSubmit}>
                  <input
                    type="text"
                    placeholder={t({ en: 'Your Name', de: 'Ihr Name', fa: 'نام شما' }, lang)}
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    disabled={isSubmitting}
                    required
                  />
                  <input
                    type="email"
                    placeholder={t({ en: 'Your Email', de: 'Ihre E-Mail', fa: 'ایمیل شما' }, lang)}
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    disabled={isSubmitting}
                    required
                  />
                  <textarea
                    placeholder={t({ en: 'Your Message', de: 'Ihre Nachricht', fa: 'پیام شما' }, lang)}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    rows="3"
                    disabled={isSubmitting}
                    required
                  ></textarea>
                  <button type="submit" className="footer-contact-submit" disabled={isSubmitting}>
                    {isSubmitting ? t({ en: 'Sending...', de: 'Wird gesendet...', fa: 'در حال ارسال...' }, lang) : t({ en: 'Send Message', de: 'Nachricht senden', fa: 'ارسال پیام' }, lang)}
                  </button>
                  {submitStatus && (
                    <div className={`footer-contact-status ${submitStatus.type}`}>
                      {submitStatus.message}
                    </div>
                  )}
                </form>
                <div className="footer-contact-info">
                  <a href={`mailto:${content.links?.contact?.email}`}>{content.links?.contact?.email}</a>
                  <span>{t(content.links?.contact?.city, lang)}</span>
                  <span>{t(content.links?.contact?.country, lang)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-legal">
              <p>{t(content.legal?.copyright, lang)}</p>
              <div className="legal-links">
                {content.legal?.links?.map((link, idx) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && <span>•</span>}
                    {renderInternalLink(link.href, t(link.label, lang))}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="footer-badge">
              <span className="badge-globe">●</span>
              <span>{t(content.badge?.text, lang)}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
